import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  LinearProgress,
  Pagination,
  Avatar,
  Badge,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Create authenticated axios instance
const authenticatedAxios = (() => {
  const api = axios.create({
    baseURL: '/api',
    headers: { 'Content-Type': 'application/json' }
  });
  
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  return api;
})();

// API functions using authenticated axios
const api = {
  getStudents: (params) => authenticatedAxios.get('/students', { params }).then(res => res.data),
  searchStudents: (searchTerm, groupId, page = 1, pageSize = 40) => {
    const params = { page, limit: pageSize };
    if (searchTerm) params.search = searchTerm;
    if (groupId && groupId !== 'all') params.group_id = groupId;
    return authenticatedAxios.get('/students', { params }).then(res => res.data);
  },
  getStudent: (id) => authenticatedAxios.get(`/students/${id}`).then(res => res.data),
  createStudent: (data) => authenticatedAxios.post('/students', data).then(res => res.data),
  updateStudent: (id, data) => authenticatedAxios.put(`/students/${id}`, data).then(res => res.data),
  deleteStudent: (id) => authenticatedAxios.delete(`/students/${id}`).then(res => res.data),
  getGroups: () => authenticatedAxios.get('/groups?limit=1000').then(res => res.data),
  getStudentStats: async () => {
    try {
      const [studentsRes, groupsRes, coursesRes] = await Promise.all([
        authenticatedAxios.get('/students'),
        authenticatedAxios.get('/groups'),
        authenticatedAxios.get('/courses')
      ]);
      
      return {
        data: {
          total_students: studentsRes.data.pagination?.total_items || studentsRes.data.data?.length || (Array.isArray(studentsRes.data) ? studentsRes.data.length : 0),
          active_students: (studentsRes.data.data || studentsRes.data || []).filter(s => s.is_active !== false).length || 0,
          total_groups: groupsRes.data.pagination?.total_items || groupsRes.data.data?.length || (Array.isArray(groupsRes.data) ? groupsRes.data.length : 0),
          total_courses: coursesRes.data.pagination?.total_items || coursesRes.data.data?.length || (Array.isArray(coursesRes.data) ? coursesRes.data.length : 0)
        }
      };
    } catch (error) {
      console.error('Error fetching student stats:', error);
      return { data: { total_students: 0, active_students: 0, total_groups: 0, total_courses: 0 } };
    }
  }
};

// Student Form Dialog
const StudentFormDialog = ({ open, onClose, student, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    idnumber: '',
    role: 'student',
    phone1: '',
    student_id: '',
    groups: [],
    ...student
  });

  // Get available groups
  const { data: groupsData } = useQuery('groups', api.getGroups);
  const availableGroups = groupsData?.data || groupsData || [];

  React.useEffect(() => {
    if (student) {
      // Get current groups for student
      const currentGroups = student.group_names 
        ? student.group_names.split(',').map(name => name.trim()) 
        : [];
      
      // Match group names to group objects
      const selectedGroups = availableGroups.filter(group => 
        currentGroups.includes(group.group_name || group.name)
      );
      
      setFormData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        email: student.email || '',
        username: student.username || '',
        idnumber: student.idnumber || '',
        role: student.role || 'student',
        phone1: student.phone1 || '',
        student_id: student.student_id || '',
        groups: selectedGroups
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        idnumber: '',
        role: 'student',
        phone1: '',
        student_id: '',
        groups: []
      });
    }
  }, [student, availableGroups]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Include group IDs in form data
    const submitData = {
      ...formData,
      group_ids: (formData.groups || []).map(group => group.id)
    };
    onSave(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {student ? 'Edit Student' : 'Add New Student'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID Number"
                value={formData.idnumber}
                onChange={(e) => setFormData(prev => ({ ...prev, idnumber: e.target.value }))}
                helperText="Student ID number from Moodle (supports all characters)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  label="Role"
                >
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone1}
                onChange={(e) => setFormData(prev => ({ ...prev, phone1: e.target.value }))}
                helperText="Primary phone number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Student ID"
                value={formData.student_id}
                onChange={(e) => setFormData(prev => ({ ...prev, student_id: e.target.value }))}
                helperText="Internal student identification"
              />
            </Grid>
            
            {/* Groups Multi-Select */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={availableGroups}
                value={formData.groups || []}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, groups: newValue }));
                }}
                getOptionLabel={(option) => `${option.group_name || option.name} (${option.course_name || 'No Course'})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.group_name || option.name}
                      color="primary"
                      size="small"
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Groups"
                    placeholder={formData.groups?.length ? "" : "Select groups..."}
                    helperText={`Student will be assigned to ${formData.groups?.length || 0} groups. Click outside or press ESC to close.`}
                  />
                )}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    minHeight: '56px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }
                }}
                ListboxProps={{
                  style: { maxHeight: '300px' }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {student ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Student Details Dialog
const StudentDetailsDialog = ({ open, onClose, student }) => {
  if (!student) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          Student Details: {student.first_name} {student.last_name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" mb={3}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                {student.first_name?.[0]}{student.last_name?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {student.first_name} {student.last_name}
                </Typography>
                <Chip 
                  label={student.role || 'student'} 
                  size="small" 
                  color={student.role === 'teacher' ? 'secondary' : 'primary'}
                />
              </Box>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Email</Typography>
              <Box display="flex" alignItems="center">
                <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body1">{student.email || '-'}</Typography>
              </Box>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">ID Number</Typography>
              <Typography variant="body1">{student.idnumber || '-'}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Τηλέφωνο</Typography>
              <Typography variant="body1">{student.phone1 || '-'}</Typography>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Moodle ID</Typography>
              <Typography variant="body1">{student.moodle_id || '-'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Groups</Typography>
              {student.group_names ? (
                <Box display="flex" alignItems="center" mt={1}>
                  <GroupIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                  <Chip 
                    label={`${student.group_count || 0} groups: ${student.group_names}`} 
                    size="small" 
                    color="primary"
                  />
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">No groups assigned</Typography>
              )}
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Sync Status</Typography>
              <Box display="flex" alignItems="center">
                {student.sync_status === 'success' ? (
                  <>
                    <CheckCircleIcon sx={{ mr: 1, fontSize: 16, color: 'success.main' }} />
                    <Typography variant="body2" color="success.main">Synced</Typography>
                  </>
                ) : (
                  <>
                    <ErrorIcon sx={{ mr: 1, fontSize: 16, color: 'error.main' }} />
                    <Typography variant="body2" color="error.main">Not Synced</Typography>
                  </>
                )}
              </Box>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">Last Synced</Typography>
              <Typography variant="body1">
                {student.last_synced_at ? format(new Date(student.last_synced_at), 'MMM dd, yyyy HH:mm') : 'Never'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Students Component
const Students = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pageSize] = useState(40);

  // Queries
  const { data: studentsData, isLoading, error } = useQuery(
    ['students', page, searchTerm, groupFilter],
    () => api.searchStudents(searchTerm, groupFilter, page, pageSize),
    {
      // No automatic polling - refresh only on user actions or manual refresh
    }
  );

  const { data: groupsData } = useQuery('groups', api.getGroups);

  // Mutations
  const createStudentMutation = useMutation(api.createStudent, {
    onSuccess: () => {
      toast.success('Student created successfully');
      queryClient.invalidateQueries('students');
      setFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create student: ${error.response?.data?.message || error.message}`);
    }
  });

  const updateStudentMutation = useMutation(
    ({ id, data }) => api.updateStudent(id, data),
    {
      onSuccess: () => {
        toast.success('Student updated successfully');
        queryClient.invalidateQueries('students');
        setFormOpen(false);
      },
      onError: (error) => {
        toast.error(`Failed to update student: ${error.response?.data?.message || error.message}`);
      }
    }
  );

  const deleteStudentMutation = useMutation(api.deleteStudent, {
    onSuccess: () => {
      toast.success('Student deleted successfully');
      queryClient.invalidateQueries('students');
    },
    onError: (error) => {
      toast.error(`Failed to delete student: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleSave = (formData) => {
    if (selectedStudent) {
      updateStudentMutation.mutate({ id: selectedStudent.id, data: formData });
    } else {
      createStudentMutation.mutate(formData);
    }
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setFormOpen(true);
  };

  const handleView = (student) => {
    setSelectedStudent(student);
    setDetailsOpen(true);
  };

  const handleDelete = (student) => {
    if (window.confirm(`Are you sure you want to delete "${student.first_name} ${student.last_name}"?`)) {
      deleteStudentMutation.mutate(student.id);
    }
  };

  // Extract students array from API response structure
  const students = studentsData?.data || studentsData || [];
  const pagination = studentsData?.pagination || {};
  const totalPages = pagination.total_pages || Math.ceil((Array.isArray(students) ? students.length : 0) / pageSize);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load students: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: '1.5rem', color: 'rgba(0, 0, 0, 0.87)' }}>
          Student Management
        </Typography>
        <Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedStudent(null);
                setFormOpen(true);
              }}
              sx={{ mr: 1 }}
            >
              Add Student
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries('students')}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by Όνομα, Επίθετο, Email, Τηλέφωνο..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                value={groupFilter === 'all' ? null : (groupsData?.data || groupsData || []).find(g => g.id === groupFilter) || null}
                onChange={(event, newValue) => {
                  setGroupFilter(newValue ? newValue.id : 'all');
                }}
                options={[
                  { id: 'all', group_name: 'All Groups', name: 'All Groups' },
                  { id: 'ungrouped', group_name: 'Ungrouped', name: 'Ungrouped' },
                  ...(groupsData?.data || groupsData || []).sort((a, b) => (b.member_count || 0) - (a.member_count || 0))
                ]}
                getOptionLabel={(option) => option?.group_name || option?.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Groups"
                    placeholder="Type to search groups..."
                    variant="outlined"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      <Typography variant="body2">
                        {option.group_name || option.name}
                      </Typography>
                      {option.member_count !== undefined && (
                        <Chip 
                          size="small" 
                          label={`${option.member_count} students`}
                          color={option.member_count > 0 ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No groups found"
                clearOnEscape
              />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Loading */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Students Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Moodle ID</TableCell>
                  <TableCell>ΟΝΟΜΑ</TableCell>
                  <TableCell>ΕΠΙΘΕΤΟ</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>idnumber</TableCell>
                  <TableCell>Τηλέφωνο</TableCell>
                  <TableCell>Ρόλος</TableCell>
                  <TableCell>Groups</TableCell>
                  <TableCell>Sync Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {student.moodle_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {student.first_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {student.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{student.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {student.idnumber || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {student.phone1 || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={student.role || 'student'} 
                        size="small" 
                        color={student.role === 'teacher' ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      {student.group_names ? (
                        <Tooltip title={student.group_names}>
                          <Chip 
                            label={`${student.group_count || 0} groups`} 
                            size="small" 
                            color={student.group_count > 0 ? 'primary' : 'default'}
                            icon={<GroupIcon />}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No groups
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={student.sync_status || 'never'} 
                        color={student.sync_status === 'success' ? 'success' : student.sync_status === 'syncing' ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(student)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <>
                          <Tooltip title="Edit Student">
                            <IconButton size="small" onClick={() => handleEdit(student)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Student">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDelete(student)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages} • {pagination.total_items || students.length} students total
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Show pagination info even on single page if there are students */}
          {totalPages === 1 && students.length > 0 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Showing all {pagination.total_items || students.length} students
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <StudentFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        student={selectedStudent}
        onSave={handleSave}
      />

      <StudentDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        student={selectedStudent}
      />
    </Box>
  );
};

export default Students;
